import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { BorderedField, Flex } from './Flex';
import { Input } from './CustomInput';
import { ArrowBackIcon } from '../icons/ArrowBackIcon';
import { CloseFilledIcon } from '../icons/CloseFilledIcon';
import { SearchIcon } from '../icons/SearchIcon';
import { toLower, filter, includes } from 'lodash';

const StyledSelectContainer = styled(Flex).attrs({
    width: "100%",
    align: "center"
})`
    & #select-clear-icon {
        display: none;
    }
    &:hover {
        #select-clear-icon {
            display: ${({filled}) => filled && "block"};
        }
        #select-arrow-icon {
            display: ${({filled}) => filled && "none"};
        }
    }
`;

const StyledList = styled.ul`
    position: absolute;
    top: 100%;
    left: 0;

    width: 100%;
    padding: 4px 0;
    background: #FFF;
    box-shadow: 0px 6px 24px rgba(11, 44, 95, 0.15);
    border-radius: 8px;
    list-style-type: none;

    opacity: ${({visible}) => visible ? 1 : 0};
    visibility: ${({visible}) => visible ? "visible" : "hidden"};
    transition: 0.03s ease;
    transform: translate(0, 8px);
`;

const StyledListItem = styled.li`
    display: flex;
    align-items: center;
    height: 30px;
    padding: 0 11px;
    color: var(--sp-paragraph-color);
    &:hover {
        background: #F5F8FF;
    }
`;

const Select = React.forwardRef(({ bordered, options=[], onChange, ...props }, ref) => {
    const listRef = useRef(null);
    const initialState = { visible: false, value: "", context: "", cache: [] };
    const [ state, setState ] = useState(initialState);

    useEffect(() => {
        setState(state => ({ ...state, cache: options }));
        const handleOutsideClick = (e) => {
            e.stopPropagation();
            if (listRef.current && !listRef.current.contains(e.target)) {
                onCancel();
            }
        }
        document.addEventListener("mousedown", handleOutsideClick, false);

        return () => document.removeEventListener("mousedown", handleOutsideClick, false);
    }, []);

    const onCancel = () => {
        setState(state => ({ ...state, visible: false }));
    };

    const component = (
        <Flex padding={{ left: "11px", right: "11px" }} position="relative" width="100%" ref={listRef}>
            <StyledSelectContainer filled={state.context || state.value}>
                <Input
                    ref={ref}
                    name={props.name}
                    bordered={false}
                    style={{ width: "100%"}}
                    value={state.visible ? state.context : state.value}
                    placeholder={state.visible ? state.value : null}
                    onClick={(e) => {
                            e.stopPropagation();
                            if (state.value && state.context == "") {
                                setState(state => ({ ...state, visible: !state.visible }));
                            } else if (!state.visible) {
                                setState(state => ({ ...state, visible: true, context: "", cache: options }));
                            }
                        }}
                    onChange={(e) => setState(state => ({ ...state, context: e.target.value, cache: filter(options, o => includes(toLower(o.title), toLower(e.target.value))) }))}
                    onKeyUp={(e) => {if (toLower(e.key) == "escape") onCancel()}}
                />
                <span style={{ width: "24px", height: "24px", display: "flex", alignItems: "center", justifyContent: "center"}}>
                    <CloseFilledIcon id="select-clear-icon" size={16} color="var(--sp-dark-grey-blue-color)" hoverColor="var(--sp-label-color)" onClick={(e) => {e.stopPropagation(); setState({ ...initialState, cache: options }); onChange("")}} />
                </span>
                <span id="select-arrow-icon" onClick={(e) => {e.stopPropagation(); setState(state => ({ ...state, visible: !state.visible }))}}>
                    {state.visible && !state.value
                        ? <SearchIcon size={16} color="var(--sp-dark-grey-blue-color)" />
                        : <ArrowBackIcon rotate={state.visible ? 90 : 270} color="var(--sp-main-color)" />
                    }
                </span>
            </StyledSelectContainer>
            <StyledList visible={state.visible}>
                {state.cache.map(option => (
                    <StyledListItem key={option.key} onClick={(e) => {
                                e.stopPropagation();
                                onChange(option.title);
                                setState(state => ({ ...state, value: option.title, visible: false }));
                    }}>
                        {option.title}
                    </StyledListItem>
                ))}
            </StyledList>
        </Flex>
    );

    return (bordered
        ? <BorderedField hasError={props.error}>
            {component}
        </BorderedField>
        : component)
});

export { Select };